// Entry point for the Shroud landing chat bundle.
//
// Wires the config reader, UI, agent loop, and JSON-RPC tool client into a
// single browser-side chat experience. Conversation history lives only in
// the in-memory `messages` array for the lifetime of the page — it is never
// persisted, in line with the platform's confidentiality posture. A page
// reload starts a completely fresh chat.
//
// Anonymous access: no Authorization header is sent. The /rpc and
// /v1/cocoon/stream endpoints both pass anonymous requests through their
// OptionalAuth middleware. Anonymous callers see only CapRead tools and
// are IP-rate-limited at the free-plan threshold.

import { CocoonClient } from '@alphatoncapital/shroud-sdk';
import type { StoredMessage, ModelEntry } from './types.js';
import { getConfig } from './config.js';
import { executeToolCall, listTools } from './tools.js';
import { buildSystemPrompt } from './system-prompt.js';
import { runAgentLoop } from './orchestrator.js';
import type { OrchestratorCallbacks } from './orchestrator.js';
import { toOpenaiTools } from './openai-tools.js';
import { ChatUI } from './ui.js';
import {
  fetchAttestationForMessage,
  renderAttestationBadge,
  renderLoadingBadge,
  renderErrorBadge,
} from './attestation-badge.js';
import { dbg } from './debug.js';

/** Tool names the chat is allowed to invoke. */
const ALLOWED_CHAT_TOOLS = [
  'docs_search',
  'platform_listModels',
  'platform_getAttestationQuote',
];

async function init(): Promise<void> {
  const config = getConfig();
  const ui = new ChatUI();
  let currentModel = config.model;
  // History lives only in this array for the lifetime of the page. No
  // persistence — page reload = fresh chat.
  const messages: StoredMessage[] = [];

  // Derive the WebSocket base URL from the page origin. The bundle is
  // served same-origin with the API so this is always correct.
  const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsBase = `${wsProtocol}//${location.host}`;

  // No apiKey — anonymous WebSocket connections pass through OptionalAuth.
  // Attestation policy is disabled here because the chat surfaces quotes
  // separately via platform_getAttestationQuote and the badge UI.
  const client = new CocoonClient(wsBase, {
    attestationPolicy: null,
  });

  // Fetch the tool catalog and filter to the chat's allow list.
  const toolDefs = await listTools();
  const chatTools = toolDefs.filter((t) => ALLOWED_CHAT_TOOLS.includes(t.name));
  const openaiTools = toOpenaiTools(chatTools);
  const systemPrompt = buildSystemPrompt(chatTools);
  dbg('init: tool catalog', {
    rawToolCount: toolDefs.length,
    rawNames: toolDefs.map((t) => t.name),
    allowed: ALLOWED_CHAT_TOOLS,
    chatToolCount: chatTools.length,
    chatNames: chatTools.map((t) => t.name),
    openaiToolCount: openaiTools.length,
    firstOpenaiTool: openaiTools[0],
    systemPromptLen: systemPrompt.length,
  });

  // Populate the model selector when Cocoon is enabled.
  if (config.cocoonEnabled) {
    try {
      const result = await executeToolCall('platform_listModels', {});
      if (!result.error && Array.isArray(result.content)) {
        const models = result.content as ModelEntry[];
        if (models.length > 0) {
          ui.createModelSelector(models, currentModel, (newModel) => {
            currentModel = newModel;
          });
        }
      }
    } catch {
      // Model selector is non-critical; continue without it.
    }
  }

  ui.addPoweredByFooter();

  ui.setup(async (text: string) => {
    const userMsg: StoredMessage = { role: 'user', content: text };
    messages.push(userMsg);
    ui.addUserMessage(text);

    if (!config.cocoonEnabled || !currentModel) {
      ui.addError('Cocoon is temporarily unavailable. Please try again later.');
      return;
    }

    let runningIndicator: HTMLElement | null = null;

    const callbacks: OrchestratorCallbacks = {
      onStreamStart: () => {
        ui.startAssistantMessage();
      },
      onStreamChunk: (_chunk, fullContent) => {
        ui.updateStreamingContent(fullContent);
      },
      onStreamEnd: (_fullContent) => {
        // Intermediate end — the loop may continue with tool calls.
      },
      onToolCallStart: (name) => {
        runningIndicator = ui.addToolRunning(name);
      },
      onToolCallEnd: (trace) => {
        if (runningIndicator) {
          ui.removeElement(runningIndicator);
          runningIndicator = null;
        }
        ui.addToolTrace(trace);
      },
      onError: (message) => {
        ui.addError(message);
      },
      onLowConfidence: () => {
        ui.addLowConfidenceBanner();
      },
    };

    dbg('user submit ->', {
      model: currentModel,
      messageCount: messages.length,
      openaiToolCount: openaiTools.length,
      openaiToolNames: openaiTools.map((t) => t.function.name),
      userTextLen: text.length,
    });
    const assistantMsg = await runAgentLoop(
      client,
      currentModel,
      systemPrompt,
      messages,
      text,
      openaiTools,
      callbacks,
    );

    ui.endAssistantMessage(assistantMsg.content);

    messages.push(assistantMsg);

    void attachAttestationBadge(assistantMsg);
  });

  /**
   * Fetch the attestation quote for the most recent assistant turn and
   * render the badge on its bubble. The fetch happens in parallel with
   * the next user input so a slow attestation call does not block the
   * conversation.
   */
  async function attachAttestationBadge(assistantMsg: StoredMessage): Promise<void> {
    const aiBubbles = document.querySelectorAll<HTMLElement>('.hchat__bubble--ai');
    const lastBubble = aiBubbles[aiBubbles.length - 1] ?? null;
    if (!lastBubble) return;

    const loadingBadge = renderLoadingBadge(lastBubble);
    try {
      const attestation = await fetchAttestationForMessage();
      loadingBadge.remove();
      if (attestation) {
        renderAttestationBadge(lastBubble, attestation);
        assistantMsg.attestation = attestation;
      } else {
        renderErrorBadge(lastBubble);
      }
    } catch {
      loadingBadge.remove();
      renderErrorBadge(lastBubble);
    }
  }

  // Re-execute a tool call from a trace block. Edited arguments are read
  // from the textarea inside the same trace.
  document.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('hchat__trace-rerun')) return;

    const traceEl = target.closest('.hchat__trace');
    if (!traceEl) return;

    const toolName = target.dataset.toolName;
    if (!toolName) return;

    const editor = traceEl.querySelector('.hchat__trace-editor') as HTMLTextAreaElement | null;
    if (!editor) return;

    let args: Record<string, unknown>;
    try {
      args = JSON.parse(editor.value);
    } catch {
      const resultEl = traceEl.querySelector('.hchat__trace-result, .hchat__trace-error');
      if (resultEl) {
        resultEl.className = 'hchat__trace-error';
        resultEl.textContent = 'Invalid JSON in arguments';
      }
      return;
    }

    target.textContent = 'Running...';
    target.setAttribute('disabled', '');

    const result = await executeToolCall(toolName, args);

    target.textContent = 'Re-run Tool';
    target.removeAttribute('disabled');

    const resultEl = traceEl.querySelector('.hchat__trace-result, .hchat__trace-error');
    if (resultEl) {
      resultEl.className = result.error ? 'hchat__trace-error' : 'hchat__trace-result';
      resultEl.textContent = result.error
        ? result.error
        : JSON.stringify(result.content, null, 2);
    }

    const summary = traceEl.querySelector('summary');
    if (summary) {
      const textNodes = Array.from(summary.childNodes).filter(
        (n) => n.nodeType === Node.TEXT_NODE,
      );
      const lastText = textNodes[textNodes.length - 1];
      if (lastText) {
        lastText.textContent = result.error
          ? ` ${toolName} — failed: ${result.error.split(':')[0]}`
          : ` ${toolName} — ${Math.round(result.elapsedMs)}ms`;
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void init();
  });
} else {
  void init();
}
